/**
 * Hand-written narrative for the five suppliers that carry the demo story:
 * the "What you should know" insight, three scripted questions that Nami offers
 * on the supplier page, and account notes. Every other supplier
 * gets a generated insight and generic questions (supplier360-insight.ts and
 * supplier360-ask.ts).
 *
 * Citations name a document and a section only. The agreement text is owned by
 * the contract data, so quotes are never copied in here.
 */
import type { ScriptedAnswer } from '@/demo/procurement/ask/types';
import type { AiInsightTone } from '@/demo/procurement/components/AiInsight';
import { getSupplier } from '@/demo/procurement/data/suppliers';
import type { SupplierId } from '@/demo/procurement/data/types';
import {
  alertLink,
  contractLink,
  invoiceLink,
  materialLink,
  supplierLink,
} from './supplier360-links';
import type { LinkTarget, RichText } from './supplier360-links';

export interface InsightAction {
  label: string;
  link: LinkTarget;
}

export interface SupplierInsight {
  title: string;
  tone: AiInsightTone;
  bullets: RichText[];
  actions: InsightAction[];
}

export interface SupplierNarrative {
  insight: SupplierInsight;
  /** Three scripted questions, offered as chips in Nami on the supplier page. */
  questions: ScriptedAnswer[];
  /**
   * Account notes: the story facts behind this supplier. The page's "Ask" box
   * used to read them; Nami's answers cite them as `P.5` (the overview tab).
   * Nothing displays them now.
   */
  notes: string;
}

const CORVANE_SA = 'SA-CORV-2024-017';
const VITREX_QA = 'QA-VITX-2023-004';
const VITREX_SA = 'SA-VITX-2023-004';
const LUMEN_SA = 'SA-LUMN-2025-002';
const GENTLY_MSA = 'MSA-GEN-2025-001';
const HOLLIS_SA = 'SA-HOLL-2024-021';

const CORVANE = getSupplier('sup-corvane').shortName;
const HOLLIS = getSupplier('sup-hollis').shortName;
const VITREX = getSupplier('sup-vitrex').shortName;
const LUMEN = getSupplier('sup-lumen').shortName;
const KAISEI = getSupplier('sup-kaisei').shortName;
const KESTREL = getSupplier('sup-kestrel').shortName;
const GENTLY = getSupplier('sup-gently').shortName;

const CORVANE_NARRATIVE: SupplierNarrative = {
  insight: {
    title: 'Cheapest on unit price, not on total cost',
    tone: 'warning',
    bullets: [
      [
        'Supplies ',
        materialLink(
          'MAT-THHN',
          '52% of 1/0 gauge copper building wire volume',
        ),
        `. Lowest unit price at $424.36 per 100 ft, but total cost is $446.10 per 100 ft against $440.20 for ${HOLLIS} once returns, re-spooling, customer credits and rush orders are included.`,
      ],
      [
        'Credit score fell from 72 to 58 after the parent company refinanced (',
        alertLink('RSK-298'),
        '). No downgrade notice has arrived, although ',
        contractLink(CORVANE_SA, '7.3'),
        ' requires one within 5 business days and lets Pillar require a continuity plan with extra stock.',
      ],
      [
        '4 quality issues in the last 12 months (jacket defects, short reels and mislabeled lots) cost $1.1M in returns, re-spooling and customer credits, plus 31 hours of cut-to-length line downtime. The defect rate is 820 per million against the limit of 500 in ',
        contractLink(CORVANE_SA, '8.1'),
        '.',
      ],
      [
        contractLink(CORVANE_SA),
        ' ends Dec 31, 2026 with no auto-renewal, and the renewal decision due under ',
        contractLink(CORVANE_SA, '2.2'),
        ' on Oct 2 is overdue. The recommendation is to move 15 percentage points of volume to ',
        supplierLink('sup-hollis', HOLLIS),
        ' at renewal, worth $0.9M a year.',
      ],
      [
        'Invoice ',
        invoiceLink('CV-2026-08812'),
        ' ($305,900) is on hold: Net 30 against Net 45, a unit price above the price increase cap, and freight billed on a contract that already includes freight. $8,848 is recoverable.',
      ],
    ],
    actions: [
      {
        label: 'Compare copper wire suppliers',
        link: { kind: 'material', materialId: 'MAT-THHN' },
      },
      {
        label: 'Review held invoice',
        link: { kind: 'invoice', invoiceId: 'CV-2026-08812' },
      },
    ],
  },
  notes: `${CORVANE} supplies 52% of 1/0 gauge copper building wire (MAT-THHN, 7,384,000 ft a year). Unit price $424.36 per 100 ft, total cost $446.10 per 100 ft against $440.20 for ${HOLLIS} and $468.90 for ${KAISEI}. Four quality issues in the last 12 months (jacket defects, short reels and mislabeled lots) caused $1.1M of returns, re-spooling and customer credits and 31 hours of cut-to-length line downtime, and two rush orders cost $186K. The credit score fell from 72 to 58 (alert RSK-298). Invoice CV-2026-08812 is on hold in invoice compliance. The agreement ends Dec 31, 2026, and the renewal decision due Oct 2 is overdue.`,
  questions: [
    {
      question: 'Why is invoice CV-2026-08812 on hold?',
      aliases: [
        `Why is the ${CORVANE} invoice on hold?`,
        `What is wrong with the ${CORVANE} invoice?`,
      ],
      thinking: [
        'Reading invoice CV-2026-08812 against SA-CORV-2024-017',
        'Checking payment terms, unit price and delivery terms',
      ],
      answer:
        'Invoice CV-2026-08812 ($305,900.00, PO-4500213) breaks three terms of the supply agreement:\n\n- **Payment terms.** The invoice says Net 30, but §4.2 sets Net 45 from receipt of a valid invoice and says a shorter term on an invoice has no effect.\n- **Unit price.** 70,000 ft were billed at $431.50 per 100 ft. The 2026 price is capped at $424.36 per 100 ft under §3.3, so the line is overbilled by $4,998.00.\n- **Freight.** $3,850.00 of freight was billed on a contract that already includes freight. §5.1 and §5.2 make the supplier bear all freight and delivery charges to the Dallas DC and Phoenix DC.\n\nThe recoverable amount is $8,848.00, plus 15 days of payment timing on $305,900.',
      citations: [
        { documentId: CORVANE_SA, ref: '4.2' },
        { documentId: CORVANE_SA, ref: '3.3' },
        { documentId: CORVANE_SA, ref: '5.2' },
      ],
    },
    {
      question: 'What are our options at the December renewal?',
      aliases: [
        `What happens when the ${CORVANE} agreement ends?`,
        `Can we renew the ${CORVANE} agreement?`,
      ],
      thinking: [
        'Reading term, renewal and termination clauses in SA-CORV-2024-017',
        'Checking price protections and the share recommendation',
      ],
      answer: `SA-CORV-2024-017 ends on Dec 31, 2026 and does not renew automatically (§2.1), so any extension needs a signed amendment. Your options:\n\n- **Renew with a lower allocation.** §2.2 asks Pillar to give its renewal decision and volume allocation 90 days before the end of the term. That date was Oct 2, so the decision is now overdue. The recommendation on the copper wire comparison page is to move 15 percentage points of volume to ${HOLLIS} (${CORVANE} 37%, ${HOLLIS} 48%, ${KAISEI} 15%), worth $0.9M a year.\n- **Hold the 2026 price.** §3.3 caps the annual adjustment tied to the copper market price at +3%, so $424.36 per 100 ft is the ceiling for 2026. Holding it through 2027 avoids the next step up.\n- **Exit early.** §12.2 lets Pillar terminate for convenience on 180 days’ notice. ${CORVANE} has no such right.`,
      citations: [
        { documentId: CORVANE_SA, ref: '2.1' },
        { documentId: CORVANE_SA, ref: '2.2' },
        { documentId: CORVANE_SA, ref: '12.2' },
      ],
    },
    {
      question: 'How does the credit downgrade affect us?',
      aliases: [
        `What does the ${CORVANE} credit downgrade mean for supply?`,
        `What is the ${CORVANE} credit risk?`,
      ],
      thinking: [
        'Reading financial condition and termination clauses in SA-CORV-2024-017',
        'Checking alert RSK-298 and the supplier profile',
      ],
      answer: `${CORVANE}’s credit score fell from 72 to 58 after its parent company’s refinancing (RSK-298). The agreement gives Pillar some protection:\n\n- **Notice.** §7.3 requires ${CORVANE} to notify Pillar within 5 business days of any credit downgrade. No notice has arrived yet.\n- **Continuity plan.** After that notice Pillar may require a plan, satisfactory to Pillar, to assure continuity of supply. The plan can include extra stock.\n- **Exit.** §12.2 allows termination for convenience on 180 days’ notice.\n\n${CORVANE} supplies 52% of copper building wire, so requesting the plan now is worth doing before the renewal discussion.`,
      citations: [
        { documentId: CORVANE_SA, ref: '7.3' },
        { documentId: CORVANE_SA, ref: '12.2' },
        { documentId: 'profile', ref: 'P.3' },
      ],
    },
  ],
};

const VITREX_NARRATIVE: SupplierNarrative = {
  insight: {
    title: 'Critical quality problem with an open corrective action request',
    tone: 'danger',
    bullets: [
      [
        'Lot VX-24117 of 800 A breakers shipped from a factory that UL has not certified. That breaches ',
        contractLink(VITREX_QA, '7.2'),
        ' (180 days’ notice and Pillar approval before any change of manufacturing site). See ',
        alertLink('RSK-301'),
        '.',
      ],
      [
        'The lot is quarantined at the panel shop, which has 6 weeks of supply on hand. ',
        supplierLink('sup-hollis', HOLLIS),
        ' is approved as the backup source for 20% of 800 A breaker volume, as alternate part MCCB-800A-H.',
      ],
      [
        `Corrective action request SCAR-0192 is open. ${VITREX} must respond within 10 business days under `,
        contractLink(VITREX_QA, '9.1'),
        ', and ',
        contractLink(VITREX_QA, '7.5'),
        ' lets Pillar reject affected lots and recover its costs.',
      ],
      [
        'The defect rate is 1,450 per million against a limit of 500, and it spiked in the last two months after the problem lot. Panels built with breakers from an uncertified factory cannot ship under the panel shop’s UL certification.',
      ],
      [
        contractLink(VITREX_SA),
        ' ends Jun 30, 2027 with no auto-renewal, and ',
        contractLink(VITREX_SA, '5.1', '§5.1'),
        ' allows immediate termination for a change that breaches change control.',
      ],
    ],
    actions: [
      {
        label: 'Open alert RSK-301',
        link: { kind: 'alert', alertId: 'RSK-301' },
      },
      {
        label: 'Read quality agreement',
        link: { kind: 'contract', contractId: VITREX_QA, section: '7.2' },
      },
    ],
  },
  notes: `${VITREX} supplies 80% of 800 A circuit breakers (MAT-MCCB, 2,900 a year) at $3,850 each. ${HOLLIS} is the approved backup source for the other 20% at $4,020 each, as alternate part MCCB-800A-H, with a 5-week lead time against 10 weeks at ${VITREX}. Lot VX-24117 shipped from a factory that UL has not certified and is quarantined at the panel shop, which holds about 6 weeks of supply. Corrective action request SCAR-0192 is open. The defect rate is 1,450 per million over the last 12 months against a limit of 500.`,
  questions: [
    {
      question:
        'What does the quality agreement say about the undisclosed factory?',
      aliases: [
        `Did ${VITREX} breach the quality agreement?`,
        `What are our rights on the ${VITREX} factory change?`,
      ],
      thinking: [
        'Reading QA-VITX-2023-004 Article 7: change control',
        'Reading SA-VITX-2023-004 §4.2 and §5.1',
      ],
      answer: `Lot VX-24117 shipped from a factory that ${VITREX} did not disclose and that UL has not certified. That breaches change control:\n\n- **Quality agreement §7.2.** ${VITREX} may not change the manufacturing site or a subcontractor without 180 days’ prior written notice and Pillar’s written approval, and product must ship only from factories UL has certified.\n- **Quality agreement §7.5.** Pillar may reject any affected lot, whether or not it has been used, and recover its investigation, quarantine, rework, downtime and replacement costs.\n- **Supply agreement §5.1.** Pillar may terminate the supply agreement immediately on written notice for a change that breaches §4.2.\n\nThe lot is quarantined at the panel shop, so none of it has gone into a customer panel.`,
      citations: [
        { documentId: VITREX_QA, ref: '7.2' },
        { documentId: VITREX_QA, ref: '7.5' },
        { documentId: VITREX_SA, ref: '5.1' },
      ],
    },
    {
      question: `When must ${VITREX} respond to corrective action request SCAR-0192?`,
      aliases: [
        'What is the corrective action response deadline?',
        `What happens if ${VITREX} is late on the corrective action request?`,
      ],
      thinking: [
        'Reading QA-VITX-2023-004 Article 9: quality problems and corrective action',
      ],
      answer: `§9.1 of the quality agreement requires ${VITREX} to acknowledge a corrective action request within 2 business days and respond within 10 business days with a documented root cause investigation, containment actions and a corrective and preventive action plan with owners and dates. SCAR-0192 is open for lot VX-24117.\n\nIf the response is late, or the problem recurs, §9.5 lets Pillar place ${VITREX} on controlled shipping or enhanced inspection at ${VITREX}’s cost until the cause is eliminated.`,
      citations: [
        { documentId: VITREX_QA, ref: '9.1' },
        { documentId: VITREX_QA, ref: '9.5' },
      ],
    },
    {
      question: 'Can we cover 800 A breakers from another supplier?',
      aliases: [
        `Is there a fallback for ${VITREX}?`,
        'What is our 800 A breaker supply exposure?',
      ],
      thinking: [
        'Reading the supplier profile and circuit breaker sourcing notes',
        `Comparing ${HOLLIS} and ${VITREX} on price, lead time and quality`,
      ],
      answer: `Yes, in part. ${HOLLIS} is approved as the backup source for 800 A breakers, as alternate part MCCB-800A-H, and supplies 20% today:\n\n- **Price.** $4,020 each against $3,850 from ${VITREX}, which is $170 more per breaker. A full switch of 2,900 a year would add about $0.49M.\n- **Delivery and quality.** ${HOLLIS} delivers 98.1% on time and in full, with 140 defects per million and a 5-week lead time. ${VITREX} is at 1,450 defects per million and a 10-week lead time.\n- **Cover.** The panel shop holds about 6 weeks of supply, so ${HOLLIS} would need to confirm capacity for more than 20% of the volume.`,
      citations: [{ documentId: 'profile', ref: 'P.5' }],
    },
  ],
};

const LUMEN_NARRATIVE: SupplierNarrative = {
  insight: {
    title: 'Sole source for 50 HP motor drives, with tariff exposure',
    tone: 'danger',
    bullets: [
      [
        'The only source (100%) of ',
        materialLink('MAT-VFD', '50 HP 480 V motor drives'),
        ' at $5,500 each delivered to Hayward DC with duties paid, which go into the panel shop’s motor control panels and motor drive retrofit kits. See ',
        alertLink('RSK-305'),
        '.',
      ],
      [
        'Invoice ',
        invoiceLink('LB-26-3391'),
        ' ($412,500) includes an $82,500 “tariff surcharge 25%” line. ',
        contractLink(LUMEN_SA, '5.3'),
        ` puts all duties and tariffs on ${LUMEN}.`,
      ],
      [
        `${KESTREL} is the second source in onboarding (`,
        { text: 'ONB-0087', link: { kind: 'onboarding', caseId: 'ONB-0087' } },
        ', ',
        {
          text: 'RFQ-2026-022',
          link: { kind: 'sourcing', eventId: 'RFQ-2026-022' },
        },
        `). ${KESTREL} quoted $6,050 each, 10% above ${LUMEN}.`,
      ],
      [
        `${LUMEN} reports 78 of the 90 contractual days of buffer stock at its Ningbo warehouse (`,
        contractLink(LUMEN_SA, '7.1'),
        '). The agreement runs to Jun 30, 2027.',
      ],
    ],
    actions: [
      {
        label: `Open ${KESTREL} onboarding`,
        link: { kind: 'onboarding', caseId: 'ONB-0087' },
      },
      {
        label: 'Review held invoice',
        link: { kind: 'invoice', invoiceId: 'LB-26-3391' },
      },
    ],
  },
  notes: `${LUMEN} is the only source of 50 HP, 480 V motor drives (MAT-VFD, 3,400 a year) at $5,500 each delivered to Hayward DC with duties paid, shipped from Ningbo. ${KESTREL} quoted $6,050 each, duties paid, and is in onboarding as the second source (ONB-0087, RFQ-2026-022). Invoice LB-26-3391 ($412,500, 60 drives) includes an $82,500 tariff surcharge line. Alert RSK-305 covers the single-source and tariff exposure.`,
  questions: [
    {
      question: `Can ${LUMEN} pass through the new tariff?`,
      aliases: [
        `Can ${LUMEN} charge a tariff surcharge?`,
        'Is the 25% tariff surcharge allowed?',
      ],
      thinking: [
        'Reading delivery terms and duties clauses in SA-LUMN-2025-002',
        'Checking invoice LB-26-3391 line items',
      ],
      answer: `No. SA-LUMN-2025-002 has ${LUMEN} deliver to Hayward DC with duties paid (§5.1), so ${LUMEN} bears all costs of carriage and import clearance. §5.3 goes further:\n\n- All duties, tariffs and import taxes are borne by ${LUMEN} and included in the price, including any new or increased charge introduced after the effective date.\n- ${LUMEN} may not invoice them as a surcharge, pass-through or adjustment, and a change in any such charge does not let ${LUMEN} adjust the price.\n\nInvoice LB-26-3391 includes an $82,500 line for a 25% tariff surcharge on 60 drives. Pillar can pay less than billed on that line.`,
      citations: [
        { documentId: LUMEN_SA, ref: '5.1' },
        { documentId: LUMEN_SA, ref: '5.3' },
      ],
    },
    {
      question: `How much buffer stock does ${LUMEN} hold?`,
      aliases: [`What is the ${LUMEN} buffer stock requirement?`],
      thinking: ['Reading continuity of supply in SA-LUMN-2025-002 Article 7'],
      answer: `§7.1 requires ${LUMEN} to hold 90 days of Pillar’s forecast requirements as a dedicated stock of finished drives at its Ningbo warehouse, at ${LUMEN}’s cost. At 3,400 drives a year that is about 840 drives.\n\n- The stock is kept separate, stored to the manufacturer’s specification and rotated oldest first.\n- ${LUMEN} reports the quantity monthly, and Pillar may inspect it on request.\n- Title stays with ${LUMEN} until delivery, and the stock ships on an expedited basis against Pillar purchase orders.`,
      citations: [{ documentId: LUMEN_SA, ref: '7.1' }],
    },
    {
      question: 'Can we approve a second source for 50 HP motor drives?',
      aliases: [
        'Are we allowed to buy motor drives from another supplier?',
        `Is the ${LUMEN} agreement exclusive?`,
      ],
      thinking: [
        'Reading exclusivity and supply interruption clauses in SA-LUMN-2025-002',
        `Checking the ${KESTREL} onboarding case`,
      ],
      answer: `Yes. The agreement is non-exclusive and sets no minimum quantity (§2.2).\n\n- **Cooperation.** ${LUMEN} must give reasonable cooperation, at no charge, with the technical approval of an alternative source, including historical specification data.\n- **Supply failure.** If ${LUMEN} fails to supply for more than 30 days, Pillar may buy elsewhere and ${LUMEN} reimburses the reasonable excess cost (§7.3).\n\n${KESTREL} is already in onboarding (ONB-0087) and quoted $6,050 each.`,
      citations: [
        { documentId: LUMEN_SA, ref: '2.2' },
        { documentId: LUMEN_SA, ref: '7.3' },
      ],
    },
  ],
};

const GENTLY_NARRATIVE: SupplierNarrative = {
  insight: {
    title: 'Performing above its service levels on a clean contract',
    tone: 'success',
    bullets: [
      [
        'Uptime was 99.97% over the last 12 months against the 99.9% commitment in Exhibit B. No service credits have been triggered.',
      ],
      [
        'Critical and major issue response times were met on every ticket, and the SOC 2 Type II report is current.',
      ],
      [
        contractLink(GENTLY_MSA),
        ' runs to Oct 31, 2027 and auto-renews for 12 months. Notice of non-renewal is due by Sep 1, 2027, and renewal pricing is capped at 5% (',
        contractLink(GENTLY_MSA, '4.4'),
        ').',
      ],
      [
        'The request to expand with procurement agents ($120,000 a year, ',
        { text: 'REQ-1053', link: { kind: 'requests', requestId: 'REQ-1053' } },
        ') is in legal review as an amendment to the master agreement.',
      ],
      [
        'Invoice ',
        invoiceLink('GAI-2026-0912'),
        ' ($30,000) passed every contract check.',
      ],
    ],
    actions: [
      {
        label: 'Open the master agreement',
        link: { kind: 'contract', contractId: GENTLY_MSA },
      },
      {
        label: 'View expansion request',
        link: { kind: 'requests', requestId: 'REQ-1053' },
      },
    ],
  },
  notes: `${GENTLY} provides the AP automation and procurement agents platform at $360,000 a year plus a $180,000 fixed-fee implementation (statement of work 1). Uptime was 99.97% over the last 12 months against a 99.9% commitment. Critical and major issue response times were met on every ticket. The SOC 2 Type II report is current. The initial term ends Oct 31, 2027 and auto-renews for 12 months unless notice is given 60 days before.`,
  questions: [
    {
      question: `What are the service levels with ${GENTLY}?`,
      aliases: [
        `What are the ${GENTLY} service level terms?`,
        `What uptime does ${GENTLY} commit to?`,
      ],
      thinking: [
        'Reading Exhibit B: Service level agreement',
        'Reading Exhibit B service credit and chronic failure terms',
      ],
      answer:
        'Exhibit B sets four commitments:\n\n- **Uptime.** At least 99.9% each calendar month (§B.1). Scheduled maintenance is excluded only up to 4 hours a month with 72 hours’ notice.\n- **Support.** Critical issues get a response within 1 hour, around the clock, with updates every 2 hours. Major issues get 4 business hours and minor issues get 1 business day (§B.2).\n- **Credits.** 5% of the monthly fee below 99.9%, 10% below 99.5% and 25% below 99.0%, capped at 25%. Pillar must request a credit within 30 days (§B.3).\n- **Chronic failure.** Uptime below 99.0% in any 3 months of a rolling 6 lets Pillar terminate without penalty (§B.4).',
      citations: [
        { documentId: GENTLY_MSA, ref: 'B.1' },
        { documentId: GENTLY_MSA, ref: 'B.2' },
        { documentId: GENTLY_MSA, ref: 'B.3' },
        { documentId: GENTLY_MSA, ref: 'B.4' },
      ],
    },
    {
      question: `When can we exit the ${GENTLY} agreement?`,
      aliases: [
        `What is the termination notice for ${GENTLY}?`,
        `When does the ${GENTLY} agreement renew?`,
      ],
      thinking: ['Reading term and termination clauses in MSA-GEN-2025-001'],
      answer: `You have three ways out:\n\n- **At term end.** The initial term ends Oct 31, 2027 and then renews for 12 months at a time. Written notice of non-renewal is due at least 60 days before the end, which is Sep 1, 2027 (§11.1).\n- **For convenience.** Pillar may terminate the agreement or any statement of work at any time on 90 days’ written notice. ${GENTLY} refunds prepaid fees and Pillar pays for services performed, with no other termination charge (§11.2).\n- **Chronic failure.** Uptime below 99.0% in 3 months of a rolling 6 allows termination without penalty (§B.4).`,
      citations: [
        { documentId: GENTLY_MSA, ref: '11.1' },
        { documentId: GENTLY_MSA, ref: '11.2' },
        { documentId: GENTLY_MSA, ref: 'B.4' },
      ],
    },
    {
      question: 'How much can the price rise at renewal?',
      aliases: [
        `Is there a price cap on the ${GENTLY} renewal?`,
        'What is the renewal price increase limit?',
      ],
      thinking: ['Reading renewal pricing in MSA-GEN-2025-001 §4.4'],
      answer: `The platform fees are fixed for the initial term, which ends Oct 31, 2027. For a renewal term:\n\n- ${GENTLY} may raise the fees only by written notice delivered at least 120 days before the end of the term, which is Jul 3, 2027.\n- Any increase is capped at 5% of the fees in effect over the preceding 12 months. On $360,000 that is at most $18,000, or $378,000 a year.\n- If ${GENTLY} does not send a timely notice, the fees stay unchanged for the renewal term.`,
      citations: [{ documentId: GENTLY_MSA, ref: '4.4' }],
    },
  ],
};

const HOLLIS_NARRATIVE: SupplierNarrative = {
  insight: {
    title: 'Lowest-risk strategic supplier, with room to take share',
    tone: 'success',
    bullets: [
      [
        '98.1% on time and in full, with 140 defects per million, on ',
        materialLink('MAT-THHN', '1/0 gauge copper building wire'),
        ', with no quality issues in the last 12 months.',
      ],
      [
        'Capacity headroom is confirmed for the 15 percentage point shift proposed at the ',
        supplierLink('sup-corvane', CORVANE),
        ' renewal (33% to 48%, about 2.13M ft more a year).',
      ],
      [
        `Unit price is $437.00 per 100 ft against ${CORVANE}’s $424.36, but total cost is $5.90 per 100 ft lower once quality problems and rush orders are included.`,
      ],
      [
        'Approved backup on 800 A breakers (20% share) behind ',
        supplierLink('sup-vitrex', VITREX),
        ', as alternate part MCCB-800A-H at $4,020 (+$170 each). That makes Hollis the fallback while ',
        alertLink('RSK-301'),
        ' is open.',
      ],
    ],
    actions: [
      {
        label: 'Compare copper wire suppliers',
        link: { kind: 'material', materialId: 'MAT-THHN' },
      },
    ],
  },
  notes: `${HOLLIS} supplies 33% of 1/0 gauge copper building wire at $437.00 per 100 ft (fixed for the term, delivered) and 20% of 800 A circuit breakers at $4,020 each as alternate part MCCB-800A-H. On copper wire it delivers 98.1% on time and in full, with 140 defects per million, a 5-week lead time and no quality issues. Total cost is $440.20 per 100 ft against $446.10 for ${CORVANE}: quality problems $2.60 per 100 ft and rush orders $0.60 per 100 ft against $19.22 and $2.52 at ${CORVANE}. Capacity headroom is confirmed for a 15 percentage point increase in share (33% to 48%, about 2.13M ft a year).`,
  questions: [
    {
      question: `Can ${HOLLIS} take 15 more percentage points of copper wire volume?`,
      aliases: [
        `Does ${HOLLIS} have capacity for more copper wire volume?`,
        `Can ${HOLLIS} absorb the ${CORVANE} volume?`,
      ],
      thinking: [
        'Reading SA-HOLL-2024-021 pricing and lead time',
        `Checking ${HOLLIS} delivery and quality history`,
      ],
      answer: `On the current evidence, yes. ${HOLLIS} has confirmed capacity headroom for the shift proposed at the ${CORVANE} renewal, from 33% to 48% of 1/0 gauge copper building wire. That is about 2.13M ft more a year.\n\n- **Price.** $437.00 per 100 ft, fixed for the term and delivered (§2.1).\n- **Lead time.** 5 weeks from purchase order (§4.2), against 8 weeks at ${CORVANE}.\n- **Track record.** 98.1% on time and in full, 140 defects per million and no quality issues in the last 12 months.`,
      citations: [
        { documentId: HOLLIS_SA, ref: '2.1' },
        { documentId: HOLLIS_SA, ref: '4.2' },
        { documentId: 'profile', ref: 'P.5' },
      ],
    },
    {
      question: `How does ${HOLLIS} compare with ${CORVANE} once quality is included?`,
      aliases: [
        `Is ${HOLLIS} cheaper than ${CORVANE} on total cost?`,
        'What is the total cost difference?',
      ],
      thinking: [
        'Reading the supplier profile and copper wire cost build-up',
        'Comparing unit price, quality problems and rush orders',
      ],
      answer: `${HOLLIS} costs $12.64 per 100 ft more on unit price ($437.00 against $424.36) but $5.90 per 100 ft less on total cost ($440.20 against $446.10).\n\n- **Quality problems.** $2.60 per 100 ft at ${HOLLIS} against $19.22 at ${CORVANE}.\n- **Rush orders.** $0.60 per 100 ft against $2.52.\n- **Why.** ${CORVANE}’s 4 quality issues cost $1.1M in returns, re-spooling and customer credits plus 31 hours of cut-to-length downtime, and 2 rush orders cost $186K.`,
      citations: [{ documentId: 'profile', ref: 'P.5' }],
    },
    {
      question: `Is ${HOLLIS} a fallback for the ${VITREX} 800 A breakers?`,
      aliases: [
        `Can ${HOLLIS} cover circuit breakers if ${VITREX} stops?`,
        `What does ${HOLLIS} supply for the panel shop?`,
      ],
      thinking: [
        'Reading SA-HOLL-2024-021 pricing',
        `Comparing 800 A breaker terms with ${VITREX}`,
      ],
      answer: `Yes, ${HOLLIS} is the approved backup source for 800 A breakers, as alternate part MCCB-800A-H, and supplies 20% today at $4,020 each (§2.1).\n\n- **Against ${VITREX}.** $170 more per breaker than ${VITREX}’s $3,850, but 140 defects per million against 1,450 and a 5-week lead time against 10.\n- **Limit.** Capacity above a 20% share is not yet confirmed, and the panel shop holds about 6 weeks of supply while corrective action request SCAR-0192 is open.`,
      citations: [
        { documentId: HOLLIS_SA, ref: '2.1' },
        { documentId: 'profile', ref: 'P.5' },
      ],
    },
  ],
};

export const SUPPLIER_NARRATIVES: Partial<
  Record<SupplierId, SupplierNarrative>
> = {
  'sup-corvane': CORVANE_NARRATIVE,
  'sup-vitrex': VITREX_NARRATIVE,
  'sup-lumen': LUMEN_NARRATIVE,
  'sup-gently': GENTLY_NARRATIVE,
  'sup-hollis': HOLLIS_NARRATIVE,
};

export function narrativeFor(
  supplierId: SupplierId,
): SupplierNarrative | undefined {
  return SUPPLIER_NARRATIVES[supplierId];
}
